import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Heart, LogIn, Newspaper, UserPlus } from 'lucide-react';
import { publicService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Brand, Breadcrumbs, Button, EmptyState, Loader } from '../components/ui';
import { ArticleBody } from '../components/articles/ArticleBody';
import { ReadOnlyComments } from '../components/articles/Comments';
import {
  ActionButton, AuthorFooter, ReaderHeader, SharePopover, TocCard, useReadingProgress,
} from '../components/articles/Reader';
import { useForcedDarkTheme } from '../components/articles/ArticleBits';
import { headingsOf } from '../utils/articles';
import '../components/articles/Reader.css';

/**
 * Artigo público (/a/:slug) para quem não está logado: texto inteiro, números de curtidas e
 * comentários, os 2 primeiros comentários e o convite para entrar. Logado, vai para a versão completa.
 */
function PublicArticle() {
  const { slug } = useParams();
  useForcedDarkTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (loading || user) return;
    publicService.article(slug).then(setArticle).catch(() => setError(true));
  }, [slug, user, loading]);

  const headings = useMemo(() => headingsOf(article?.bodyMd), [article?.bodyMd]);
  const { pct, active } = useReadingProgress(bodyRef, headings);

  if (loading) return <Loader label="Carregando..." />;
  if (user) return <Navigate to={`/artigos/${slug}`} replace />;

  // O login volta para o artigo
  const login = () => navigate('/login', { state: { from: location.pathname } });
  const requestAccess = () => navigate('/solicitar-acesso');
  const onShare = (channel) => publicService.share(slug, channel).catch(() => {});

  const topbar = (
    <header className="ss-topbar">
      <div className="ss-topbar__inner">
        <div className="ss-topbar__left">
          <button type="button" className="ss-topbar__brand" onClick={() => navigate('/')} aria-label="Início"><Brand size="sm" /></button>
          <nav className="ss-globalnav" aria-label="Seções">
            <button type="button" className="ss-globalnav__link is-active" onClick={() => navigate('/a')}>
              <Newspaper size={16} strokeWidth={2.2} aria-hidden="true" /><span>Artigos</span>
            </button>
          </nav>
        </div>
        <div className="rd-pubactions">
          <Button variant="ghost" icon={LogIn} onClick={login}>Entrar</Button>
          <Button variant="accent" icon={UserPlus} onClick={requestAccess}>Solicitar acesso</Button>
        </div>
      </div>
    </header>
  );

  if (error) {
    return (
      <div className="ss-shell">
        {topbar}
        <main className="ss-main">
          <EmptyState
            file="artigo.xml"
            title="Este artigo não está disponível"
            description="Ele pode ser só para alunos ou ter saído do ar. Entre para ver os artigos da turma."
            action={<Button icon={LogIn} onClick={login}>Entrar</Button>}
          />
        </main>
      </div>
    );
  }

  const firstTag = article?.tags[0];

  return (
    <div className="ss-shell">
      {topbar}
      <main className="ss-main">
        {!article ? <Loader label="Abrindo artigo..." /> : (
          <div className="rd-page">
            <Breadcrumbs items={[
              { label: 'Artigos públicos', onClick: () => navigate('/a') },
              { label: firstTag ? firstTag.name : article.title },
            ]} />

            <ReaderHeader
              article={article}
              showVisibility
              actions={(
                <>
                  <ActionButton icon={Heart} className="rd-likebtn rd-likebtn--locked" onClick={login}
                                title="Entre para curtir" aria-label={`Entre para curtir (${article.likes} curtidas)`}>
                    {article.likes}
                  </ActionButton>
                  <SharePopover article={article} onShare={onShare} />
                </>
              )}
            />

            <div className="rd-grid">
              <div className="rd-col">
                {article.coverUrl && <img className="rd-cover" src={article.coverUrl} alt="" onError={(e) => { e.currentTarget.hidden = true; }} />}
                <ArticleBody className="rd-text" markdown={article.bodyMd} bodyRef={bodyRef} />

                <div className="rd-end">
                  <div className="rd-tags">
                    {article.tags.map(t => <span key={t.slug}># {t.name}</span>)}
                    <span className="rd-tags__aside"><SharePopover article={article} onShare={onShare} size="sm" /></span>
                  </div>
                </div>

                <ReadOnlyComments data={article.commentList} likes={article.likes} onLogin={login} onRequest={requestAccess} />
              </div>

              <aside className="rd-side">
                <TocCard headings={headings} pct={pct} active={active} footer={<AuthorFooter author={article.author} />} />
              </aside>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default PublicArticle;
